import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import UserPreferencesModal from './UserPreferencesModal';

// Update to include /verify-email in allowed paths
const ALLOWED_UNREGISTERED_PATHS = ['/login', '/signup', '/reset-password', '/verify-email', '/verify-2fa'];

const LoadingSpinner = () => (
  <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-[9999]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { 
    currentUser, 
    isAdmin, 
    isRegistrationComplete, 
    registrationChecked,
    saveUserPreferences,
    isEmailVerificationRequired,
    is2FARequired,
    is2FAVerified,
    twoFactorChecked
  } = useAuth();

  const { config, isLoading: isConfigLoading } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [verifyEmailRequired, setVerifyEmailRequired] = useState(null);
  const [isVerificationChecked, setIsVerificationChecked] = useState(false);
  const [require2FA, setRequire2FA] = useState(null);
  const [is2FAChecked, setIs2FAChecked] = useState(false);
  
  // Effect to check verification requirement
  useEffect(() => {
    async function checkVerificationSetting() {
      if (currentUser) {
        try {
          const isRequired = await isEmailVerificationRequired();
          setVerifyEmailRequired(isRequired);
        } catch (error) {
          // Default to true if there's an error
          setVerifyEmailRequired(true);
        } finally {
          setIsVerificationChecked(true);
        }
      } else {
        // If no user, mark as checked but null requirement
        setIsVerificationChecked(true);
      }
    }

    if (!isVerificationChecked && !ALLOWED_UNREGISTERED_PATHS.includes(location.pathname)) {
      checkVerificationSetting();
    }
  }, [currentUser, isVerificationChecked, location.pathname, isEmailVerificationRequired]);

  // Effect to handle verification and redirect properly
  useEffect(() => {
    if (currentUser && registrationChecked && isVerificationChecked && !ALLOWED_UNREGISTERED_PATHS.includes(location.pathname)) {
      
      // If email verification is required and not verified, redirect to verification page
      if (!currentUser.emailVerified && verifyEmailRequired) {
        
        // Preserve query parameters when redirecting
        const searchParams = new URLSearchParams(window.location.search);
        const redirectPath = '/verify-email' + (searchParams.toString() ? `?${searchParams.toString()}` : '');
        
        navigate(redirectPath, { 
          state: { from: location, preserveSearch: true }, 
          replace: true 
        });
      } 
      // If registration is complete, don't show preferences modal
      else if (isRegistrationComplete) {
        setShowPreferences(false);
      } 
      // If email is verified (or verification not required) AND registration is not complete, show preferences
      else {
        setShowPreferences(true);
      }
    }
  }, [currentUser, registrationChecked, isRegistrationComplete, location, navigate, verifyEmailRequired, isVerificationChecked]);

  // Effect to check 2FA requirement
  useEffect(() => {
    async function check2FASetting() {
      if (currentUser) {
        try {
          // Add a small delay to ensure currentUser is fully set
          await new Promise(resolve => setTimeout(resolve, 100));
          const isRequired = await is2FARequired(currentUser); // ← Pass currentUser explicitly
          setRequire2FA(isRequired);
        } catch (error) {
          setRequire2FA(false);
        } finally {
          setIs2FAChecked(true);
        }
      } else {
        // If no user, mark as checked
        setIs2FAChecked(true);
      }
    }
  
    if (!is2FAChecked && !ALLOWED_UNREGISTERED_PATHS.includes(location.pathname)) {
      check2FASetting();
    }
  }, [currentUser, is2FAChecked, location.pathname, is2FARequired]);

  const handlePreferencesSubmit = async (preferences) => {
    try {
      setPreferencesLoading(true);
      await saveUserPreferences(currentUser.email, preferences);
    } catch (error) {
    } finally {
      setPreferencesLoading(false);
    }
  };

  // Early return for unregistered paths
  if (ALLOWED_UNREGISTERED_PATHS.includes(location.pathname)) {
    return children;
  }

  // Show loading for registration or config checks or verification requirement check
  if (!registrationChecked || isConfigLoading || !isVerificationChecked || !twoFactorChecked || !is2FAChecked) {
    return <LoadingSpinner />;
  }

  // Authentication check - preserve query parameters when redirecting to login
  if (!currentUser) {
    // Preserve any query parameters in the redirect
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath = '/login' + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    
    return <Navigate to={redirectPath} state={{ from: location, preserveSearch: true }} replace />;
  }

  // Email verification check - skip if verification is disabled
  if (!currentUser.emailVerified && verifyEmailRequired) {
    // Preserve any query parameters in the redirect
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath = '/verify-email' + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    
    return <Navigate to={redirectPath} state={{ from: location, preserveSearch: true }} replace />;
  }

  if (require2FA && !is2FAVerified) {
    // Preserve any query parameters in the redirect
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath = '/verify-2fa' + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    
    return <Navigate to={redirectPath} state={{ from: location, preserveSearch: true }} replace />;
  }

  // Registration check - always show preferences modal if registration is incomplete,
  // regardless of verification status
  if (!isRegistrationComplete) {
    // Note: We don't redirect here, just show a modal over the current URL
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-[9999]">
        <UserPreferencesModal
          isOpen={true}
          onClose={() => null}
          onSubmit={handlePreferencesSubmit}
          isLoading={preferencesLoading}
        />
      </div>
    );
  }

  return React.cloneElement(children, { isAdmin });
};

export default PrivateRoute;